import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { getSalesId, getSalesValue, useSalesEndpoints } from '../hooks/useSalesApiResource';
import { usePortalApiRequest } from '../../../redux/api/portalApi';

const emptyUnit = {
  id: 0,
  stationId: '',
  configurationId: '',
  productTypeId: '',
  totalGPM: 0,
  disChargePSI: 0,
  zones: 1,
  dynamicIntelPSI: '',
  feetOfLift: '',
};

const normalizeUnit = (unit = {}) => ({
  ...unit,
  id: Number(getSalesId(unit) || 0),
  stationId: getSalesValue(unit, 'stationId', 'StationId') ?? '',
  configurationId: getSalesValue(unit, 'configurationId', 'ConfigurationId') ?? '',
  productTypeId: getSalesValue(unit, 'productTypeId', 'ProductTypeId') ?? '',
  totalGPM: getSalesValue(unit, 'totalGPM', 'TotalGPM') ?? 0,
  disChargePSI: getSalesValue(unit, 'disChargePSI', 'DisChargePSI') ?? 0,
  zones: getSalesValue(unit, 'zones', 'Zones') ?? 1,
  dynamicIntelPSI: getSalesValue(unit, 'dynamicIntelPSI', 'DynamicIntelPSI') ?? '',
  feetOfLift: getSalesValue(unit, 'feetOfLift', 'FeetOfLift') ?? '',
});

const toApiPayload = (unit) => ({
  ...unit,
  Id: unit.id || undefined,
  StationId: unit.stationId === '' ? null : Number(unit.stationId),
  ConfigurationId: unit.configurationId === '' ? null : Number(unit.configurationId),
  ProductTypeId: unit.productTypeId === '' ? null : Number(unit.productTypeId),
  TotalGPM: Number(unit.totalGPM) || null,
  DisChargePSI: Number(unit.disChargePSI) || null,
  Zones: Number(unit.zones) || null,
  DynamicIntelPSI: unit.dynamicIntelPSI === '' ? null : Number(unit.dynamicIntelPSI),
  FeetOfLift: unit.feetOfLift === '' ? null : Number(unit.feetOfLift),
});

const UnitDetail = () => {
  const { id } = useParams();
  const isNew = !id;
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [unit, setUnit] = useState(emptyUnit);
  const [loading, setLoading] = useState(!isNew);
  const [saved, setSaved] = useState(false);
  const [pageError, setPageError] = useState('');
  const [saving, setSaving] = useState(false);

  const title = useMemo(() => (isNew ? 'New Unit' : `Unit Detail ${id}`), [id, isNew]);

  const loadUnit = useCallback(async () => {
    if (isNew) {
      setUnit(emptyUnit);
      setLoading(false);
      return;
    }

    setLoading(true);
    setPageError('');
    try {
      const response = await request({ method: 'GET', url: endpoints.stationTypes.byId(id) });
      setUnit(normalizeUnit(response));
    } catch {
      setPageError('Unable to load unit detail.');
    } finally {
      setLoading(false);
    }
  }, [endpoints, id, isNew, request]);

  useEffect(() => {
    loadUnit();
  }, [loadUnit]);

  const update = (field, value) => {
    setSaved(false);
    setUnit((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setPageError('');

    try {
      const payload = toApiPayload(unit);
      if (unit.id) {
        await request({ method: 'PUT', url: endpoints.stationTypes.byId(unit.id), data: payload });
      } else {
        const response = await request({ method: 'POST', url: endpoints.stationTypes.base, data: payload });
        setUnit(normalizeUnit(response));
      }
      setSaved(true);
    } catch {
      setPageError('Unable to save unit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-unit-detail-title">
      <Row className="mb-3">
        <Col><h4 id="sales-unit-detail-title">{title}</h4></Col>
        <Col className="text-end">
          <Button as={Link} to="/sales/units" variant="secondary" className="me-2">Back</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save'}</Button>
        </Col>
      </Row>
      {saved && <Alert variant="success">Unit saved.</Alert>}
      {pageError && <Alert variant="danger">{pageError}</Alert>}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading unit...</div>
          ) : (
            <Row>
              <Col md={4} className="mb-3">
                <Form.Label>Station ID</Form.Label>
                <Form.Control value={unit.stationId} onChange={(event) => update('stationId', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Configuration ID</Form.Label>
                <Form.Control value={unit.configurationId} onChange={(event) => update('configurationId', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Product Type ID</Form.Label>
                <Form.Control value={unit.productTypeId} onChange={(event) => update('productTypeId', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Total GPM</Form.Label>
                <Form.Control type="number" value={unit.totalGPM} onChange={(event) => update('totalGPM', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Discharge PSI</Form.Label>
                <Form.Control type="number" value={unit.disChargePSI} onChange={(event) => update('disChargePSI', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Zones</Form.Label>
                <Form.Control type="number" value={unit.zones} onChange={(event) => update('zones', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Dynamic Intel PSI</Form.Label>
                <Form.Control type="number" value={unit.dynamicIntelPSI} onChange={(event) => update('dynamicIntelPSI', event.target.value)} />
              </Col>
              <Col md={4} className="mb-3">
                <Form.Label>Feet Of Lift</Form.Label>
                <Form.Control type="number" value={unit.feetOfLift} onChange={(event) => update('feetOfLift', event.target.value)} />
              </Col>
            </Row>
          )}
        </div>
      </div>
    </section>
  );
};

export default UnitDetail;
